import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Button } from "./ui/button";
import { useLanguage } from "../contexts/LanguageContext";

const CookieBanner = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem("monaco_cookie_consent");
    if (!cookieConsent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem("monaco_cookie_consent", "accepted");
    localStorage.setItem("monaco_cookie_date", new Date().toISOString());
    setIsVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem("monaco_cookie_consent", "declined");
    localStorage.setItem("monaco_cookie_date", new Date().toISOString());
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed bottom-4 left-4 right-20 sm:left-auto sm:right-24 sm:bottom-6 lg:right-28 z-30 max-w-xs animate-in slide-in-from-bottom-5 duration-300"
      data-testid="cookie-banner"
    >
      <div className="bg-monaco-bg-1/95 backdrop-blur-lg border border-white/10 rounded-xl shadow-2xl p-4">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 rounded-lg bg-monaco-red/10 flex items-center justify-center flex-shrink-0">
            <Cookie className="w-4 h-4 text-monaco-red" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-monaco-text-0 text-sm">
              {language === "de" ? "Cookie-Einstellungen" : "Cookie Settings"}
            </h3>
            <p className="text-xs text-monaco-text-2 mt-1 leading-relaxed">
              {language === "de" 
                ? "Wir verwenden Cookies für eine bessere Erfahrung."
                : "We use cookies for a better experience."
              }
            </p>
          </div>
        </div>

        {/* Privacy Link */}
        <Link 
          to="/datenschutz" 
          className="text-xs text-monaco-red hover:underline mb-3 inline-block"
        >
          {language === "de" ? "Datenschutzerklärung" : "Privacy Policy"} →
        </Link>

        {/* Buttons */}
        <div className="flex gap-2">
          <Button
            onClick={handleDecline}
            variant="outline"
            size="sm"
            className="flex-1 h-8 text-xs border-white/10 text-monaco-text-1 hover:bg-white/5"
            data-testid="cookie-decline"
          >
            {language === "de" ? "Ablehnen" : "Decline"}
          </Button>
          <Button
            onClick={handleAccept}
            size="sm"
            className="flex-1 h-8 text-xs bg-monaco-red hover:bg-monaco-red-hover text-white"
            data-testid="cookie-accept"
          >
            {language === "de" ? "Annehmen" : "Accept"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
